/**
 * @windchime/shared — types and units shared across engine, clients, server, allocator.
 * This is the single source of truth for data shapes; nothing here has any I/O.
 */

/** Normalised weather, as produced by the Weather Gateway (see 03_architecture.md §4). */
export interface WeatherSnapshot {
  /** mean wind, km/h */
  windSpeed: number;
  /** degrees 0..360, direction the wind comes FROM (N=0, E=90) */
  windDirFrom: number;
  /** gust factor = gust/mean, >= 1 */
  gust: number;
  /** temperature, °F */
  tempF: number;
  /** 0..24 local to the chosen location (client derives from the snapshot's timezone) */
  localHour: number;
}

/** Tier A musical event (see 01_interface_spec.md §5). velocity is 0..1. */
export interface NoteEvent {
  midi: number;
  velocity: number;
  /** engine sim-time (seconds) at which the note fired */
  tSim: number;
}

/** One note's position on the note ring, for renderers. */
export interface RingNode {
  midi: number;
  /** radians; live position around the ring (includes tube sway in contact mode) */
  angle: number;
  isRoot: boolean;
  /** 0..1 recent-strike glow, decays over time */
  flash: number;
  /** un-swayed rest angle (contact mode); equals `angle` in poisson mode */
  restAngle?: number;
  /** live contact radius = barRadius + outward recoil (contact mode) */
  radius?: number;
}

/** Everything a renderer needs to draw a frame. Read-only snapshot. */
export interface ViewState {
  /** the clapper (striking body). `plate` is kept as a back-compat alias of `clapper`. */
  plate: { x: number; y: number };
  clapper: { x: number; y: number };
  /** the wind sail (the body that catches wind and drives the clapper); (0,0) in poisson mode */
  sail: { x: number; y: number };
  swingAzimuth: number;
  /** 0..1 */
  swingMagnitude: number;
  arcHalfWidth: number;
  /** normalized radius at which the tube faces sit (clapper contacts here) */
  barRadius: number;
  /** max clapper reach (taut cord) — the radius the clapper centre can swing to */
  clapperReach: number;
  /** clapper puck radius (ring-normalized): bigger reaches/strikes more tubes at once */
  clapperRadius: number;
  /** angular half-width of a tube face, radians — the clapper's catch zone per tube */
  barHalfWidth: number;
  /** suspension cord lengths in metres (L = g/ω²), so a side view can draw them to scale */
  cordLen: { clapper: number; sail: number };
  /** wind-sail plate orientation, radians (contact mode) — the plate swings AND spins */
  sailAngle: number;
  /** which strike engine is running */
  strikeModel: 'poisson' | 'contact';
  /** the most recent contact this frame (contact mode), for a strike spark */
  lastContact: { bar: number; impact: number; tSim: number } | null;
  /** instantaneous wind, km/h */
  instWind: number;
  /** 0..1 time-of-day activity ceiling */
  activity: number;
  rootPc: number;
  mode: { name: string; index: number };
  ring: RingNode[];
}

/** Tunable parameters (the §9 table of 02_weather_to_music_mapping.md). */
export interface EngineParams {
  densityScale: number;
  /** strikes/sec at full wind */
  maxRate: number;
  /** velocity clamp, 0..1 */
  velFloor: number;
  velCeil: number;
  gustSensitivity: number;
  turbulenceFreq: number;
  /** active-arc half width, radians */
  arcHalfWidth: number;
  /** plate restoring spring */
  spring: number;
  /** plate damping */
  damping: number;
  /** wind below this (km/h) is treated as silence */
  lullSilenceKmh: number;
  /** min seconds between auto mode changes */
  modeChangeCooldownS: number;
  /** root pitch class 0..11 (3 = D#/E♭) */
  rootPc: number;
  /** how many chimes (notes) per mode — a real wind chime has ~5-6 */
  noteCount: number;
  /** octave the chimes sit in (5 => root at E♭5) */
  rootOctave: number;

  // --- v2 contact strike model (docs/05_strike_physics_spec.md). The fields below
  //     drive the 'contact' model; the legacy spring/damping/arcHalfWidth/maxRate/
  //     densityScale above drive 'poisson'. `driveScale` is shared. ---

  /** which strike engine runs: statistical 'poisson' (default) or physical 'contact' */
  strikeModel: 'poisson' | 'contact';
  /** wind -> clapper drive multiplier (default 2.4 reproduces prior behavior exactly) */
  driveScale: number;

  // suspension cords as gravity pendulums (ω = sqrt(g/L); cord length L = g/ω²)
  /** clapper sway angular frequency, rad/s */
  clapperOmega: number;
  /** sail-on-clapper sway angular frequency, rad/s */
  sailOmega: number;
  /** tube sway/recoil angular frequency, rad/s */
  tubeOmega: number;
  /** gravity constant for the L <-> ω interpretation */
  gravity: number;

  // body masses (relative units)
  /** base clapper mass at the reference puck radius; effective mass scales with radius² */
  clapperMass: number;
  sailMass: number;
  tubeMass: number;
  /** clapper puck radius (ring-normalized). Bigger = more reach (strikes more tubes at once)
   *  and more inertia (needs more wind, swings slower); smaller leans on the sail to reach. */
  clapperRadius: number;

  // sail aerodynamics
  /** combined ½·ρ·Cd·A drag coefficient on the sail */
  sailDrag: number;
  /** small direct wind drag on the clapper */
  clapperDrag: number;
  /** 0..1, bleed drive as the sail aligns edge-on to the wind */
  sailFeather: number;
  // sail as a rectangular plate that swings AND spins about the cord axis
  /** torque turning the plate to face the relative wind (catch max); drives the spin */
  sailTorque: number;
  /** rotational damping on the plate's spin */
  sailRotDamping: number;
  /** 0..1, how much the plate's broadside↔edge-on orientation modulates the catch */
  sailFlutter: number;
  /** aerodynamic lift coefficient: a plate at an angle of attack steers the drive sideways,
   *  so as it spins/lags the wind the clapper's sweep wanders — the "seeming randomness" */
  sailLift: number;
  /** km/h -> sim wind-velocity scale */
  windDrive: number;
  /** wind-direction wander amplitude, radians (gusts swirl; spreads strikes across tubes) */
  windWander: number;

  // damping (per-second linear)
  clapperDamping: number;
  sailDamping: number;
  tubeDamping: number;

  // tube recoil
  /** outward kick per unit impact */
  tubeRecoil: number;
  /** fraction of recoil applied tangentially */
  tubeTangentialRecoil: number;

  // contact geometry
  /** rest contact radius of the tube faces (normalized) */
  barRadius: number;
  /** angular half-width of a tube face, radians */
  barHalfWidth: number;
  /** max clapper reach (taut clapper cord) — bounds the swing so wind holds it among the tubes */
  clapperReach: number;
  /** max sail offset from the clapper (taut sail cord) */
  sailReach: number;

  // contact / bounce
  /** radial bounce energy retained, 0..1 */
  restitution: number;
  /** tangential speed lost per contact, 0..1 */
  tangentialFriction: number;
  /** glancing-speed contribution to impact, 0..1 */
  tangentialCoupling: number;

  // strike -> note mapping
  /** impact-speed -> velocity scale */
  velGain: number;
  /** impacts mapping below this emit no note */
  minImpactVel: number;
  /** per-tube cooldown between emitted strikes, seconds */
  barRefractoryS: number;
  /** @deprecated superseded by rehitTauS/rehitFloor — window in which a re-hit is damped, seconds */
  rehitWindowS: number;
  /** @deprecated superseded by rehitTauS/rehitFloor — velocity multiplier for damped re-hits, 0..1 */
  rehitDamping: number;
  /** continuous re-hit recovery time constant, seconds: a tube returns to full loudness
   *  ~2–3·τ after its last strike (replaces the rehitWindowS cliff) */
  rehitTauS: number;
  /** floor a rapid re-hit is scaled to (0..1) right after a strike, before recovery */
  rehitFloor: number;

  // integrator
  /** fixed integration step (contact mode only), seconds */
  physicsSubstepS: number;
  /** optional RNG velocity micro-jitter (0 = deterministic) */
  contactJitter: number;
}

export const DEFAULT_PARAMS: EngineParams = {
  densityScale: 1,
  maxRate: 3.4,
  velFloor: 0.08,
  velCeil: 1,
  gustSensitivity: 1,
  turbulenceFreq: 1,
  arcHalfWidth: Math.PI * 0.55,
  spring: 6.5,
  damping: 2.4,
  lullSilenceKmh: 1.5,
  modeChangeCooldownS: 2.5,
  rootPc: 3,
  noteCount: 6,
  // E♭5 — calibrated by ear against the physical glockenspiel: this register sounds
  // like the instrument's middle. The pitch control moves ±1 octave around it.
  rootOctave: 5,

  // v2 contact strike model (docs/05). Defaults keep 'poisson' behavior unchanged.
  strikeModel: 'poisson',
  driveScale: 2.4, // == the previous inline multiplier, so Poisson output is identical
  // calibrated (scripts/calibrate-contact.mjs): silent lull, onset ~12 km/h,
  // ~1.6 notes/s plateau over ~3 tubes (the wind-selected active arc), full velocity range.
  clapperOmega: 6.0,
  sailOmega: 4.0,
  tubeOmega: 3.0,
  gravity: 9.81,
  clapperMass: 1.0,
  sailMass: 1.2,
  tubeMass: 2.0,
  clapperRadius: 0.24, // tuned default; effective mass = clapperMass·(radius/REF_CLAPPER_RADIUS=0.12)
  sailDrag: 2.2,
  clapperDrag: 0.05,
  sailFeather: 0,
  sailTorque: 0.4, // terminal spin rate ≈ sailTorque·relMag/sailRotDamping (faster in more wind)
  sailRotDamping: 2.0,
  sailFlutter: 0.35,
  sailLift: 0.25,
  windDrive: 1.6,
  windWander: 1.1,
  clapperDamping: 1.2,
  sailDamping: 1.5,
  tubeDamping: 3.0,
  tubeRecoil: 0.04,
  tubeTangentialRecoil: 0.5,
  barRadius: 1.0, // tubes sit at the visual ring; the clapper swings out to reach them
  barHalfWidth: 0.5,
  clapperReach: 1.0, // the tube ring is the clapper's hard limit (it bounces, not pins past)
  sailReach: 1.6,
  restitution: 0.1,
  tangentialFriction: 0.25,
  tangentialCoupling: 0.5,
  velGain: 3.0, // soft-knee slope kᵥ in vel = velFloor+(velCeil−velFloor)·tanh(kᵥ·impact)
  minImpactVel: 0.03,
  barRefractoryS: 0.04,
  rehitWindowS: 0.18, // deprecated (unused) — kept for back-compat
  rehitDamping: 0.5, // deprecated (unused) — kept for back-compat
  rehitTauS: 0.8,
  rehitFloor: 0.35,
  physicsSubstepS: 1 / 240,
  contactJitter: 0,
};

/**
 * Contact-physics MACROS — the player-facing tuning surface, and the seed for the
 * eventual "wind data drives the model" mode (interpolate macros by wind level, then
 * hide the sliders). Three knobs, each 0..1:
 *   wind — sensitivity to the wind data: low = only strong wind wakes the chime;
 *          high = even light air plays it
 *   mass — how heavy the assembly responds: heavy (1) = slow tube return, little
 *          recoil/bounce, sparse damped strikes; light (0) = lively rattle & chatter
 *   size — scale of the clapper puck + sail area: big catches more wind and strikes
 *          more tubes at once (but swings slower)
 * The mapping bakes in the calibrated values for everything else (sail spin, velocity
 * curve, impact threshold). `Breezy` reproduces DEFAULT_PARAMS.
 */
export interface ContactMacros {
  wind: number;
  mass: number;
  size: number;
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * Math.max(0, Math.min(1, t));

export function contactMacroParams(m: ContactMacros): Partial<EngineParams> {
  return {
    windDrive: lerp(0.3, 2.4, m.wind),
    tubeOmega: lerp(6.5, 2.5, m.mass),
    tubeRecoil: lerp(0.14, 0.02, m.mass),
    restitution: lerp(0.4, 0.05, m.mass),
    clapperRadius: lerp(0.06, 0.34, m.size),
    sailDrag: lerp(1.2, 2.8, m.size),
    // calibrated constants folded into the macro surface
    sailFlutter: 0.35,
    velGain: 3.0,
    minImpactVel: 0.03,
  };
}

export const CONTACT_PRESETS: Array<{ label: string; macros: ContactMacros }> = [
  { label: 'Calm', macros: { wind: 0.29, mass: 0.95, size: 0.35 } },
  { label: 'Breezy', macros: { wind: 0.62, mass: 0.85, size: 0.64 } }, // == DEFAULT_PARAMS
  { label: 'Storm', macros: { wind: 0.81, mass: 0.3, size: 0.93 } },
];

// --- the physical glockenspiel (gallery hardware) ---------------------------

/** Royal Percussion Concert RGC 3030, 3 octaves / 37 bars F-to-F. The catalog
 *  says "f2–f5" (German notation, F5..F8) but MEASURED recordings of the actual
 *  instrument (2026-08) put it exactly one octave lower: sounding F4..F7, tuned
 *  A440 within ±0.04 st. These constants describe the measured instrument. */
export const GLOCK_LOW_MIDI = 65; // F4 (measured 349.0 Hz)
export const GLOCK_HIGH_MIDI = 101; // F7
export const rootMidiOf = (pc: number, octave: number): number => 12 * (octave + 1) + pc;
/** The whole 6-note ring (root + up to an octave) must fit on the instrument. */
export const rootInGlockRange = (rootMidi: number): boolean =>
  rootMidi >= GLOCK_LOW_MIDI && rootMidi + 11 <= GLOCK_HIGH_MIDI;

// --- Weather Gateway DTOs (server -> clients) ------------------------------

/** A geocoded place returned by /geocode. */
export interface GeocodeResult {
  name: string;
  admin1?: string;
  country?: string;
  lat: number;
  lon: number;
  timezone: string;
}

/** The physical sensor a reading came from (null when the source is modeled). */
export interface WeatherStation {
  id: string;
  name: string;
  distanceKm: number;
}

/**
 * Normalised weather for a location, from /weather. Clients derive the engine's
 * WeatherSnapshot from this (computing localHour from `timezone`).
 * `gust` is a factor (gust/mean, >= 1) to match the engine; units: km/h, °F, deg-from.
 */
export interface WeatherResponse {
  windSpeed: number;
  windDirFrom: number;
  gust: number;
  tempF: number;
  timezone: string;
  /** ISO timestamp of the observation, or null for modeled data */
  observedAt: string | null;
  source: 'nws' | 'open-meteo';
  station: WeatherStation | null;
  location: { lat: number; lon: number; label?: string };
}
